import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShoppingBag, Trash2, ArrowLeft, Loader2, Tag, X, Check, Mail, User, Phone, Gift, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GiftCardCart() {
  const { items, removeItem, clearCart, totalAmount, itemCount, incrementItem, decrementItem } = useCart();
  const navigate = useNavigate();

  // Buyer info (shared across all items)
  const [buyer, setBuyer] = useState({ buyer_name: '', buyer_email: '', buyer_phone: '' });
  const [submitting, setSubmitting] = useState(false);

  // Promo code
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  // If the cart total changes (qty +/- / remove), re-validate the promo against the new amount.
  // This avoids a stale `final_amount` snapshot.
  const promoCode = appliedPromo?.code || null;
  useEffect(() => {
    if (!promoCode) return;
    if (totalAmount <= 0) {
      setAppliedPromo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/promo-codes/validate`, { code: promoCode, amount: totalAmount });
        if (!cancelled) setAppliedPromo(res.data);
      } catch {
        if (!cancelled) {
          setAppliedPromo(null);
          setPromoError('Le code promo n\'est plus valide pour ce montant');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount, promoCode]);

  const handleApplyPromo = async () => {
    setPromoError('');
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      setPromoError('Saisissez un code');
      return;
    }
    if (totalAmount <= 0) {
      setPromoError('Le panier est vide');
      return;
    }
    setValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, { code, amount: totalAmount });
      setAppliedPromo(res.data);
      toast.success(`Code "${res.data.code}" appliqué : -${res.data.amount_off.toFixed(2)} €`);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(err.response?.data?.detail || 'Code invalide');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    if (!buyer.buyer_name.trim() || !buyer.buyer_email.trim() || !buyer.buyer_phone.trim()) {
      toast.error('Veuillez remplir vos coordonnées');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.flatMap((it) => {
          const qty = it.quantity || 1;
          const card = {
            service_ids: it.services.map((s) => s.id),
            duo_service_ids: Object.keys(it.duoServices || {}).filter((k) => it.duoServices[k]),
            recipient_name: it.recipient_name,
            personal_message: it.personal_message || '',
          };
          // Expand quantity into N independent cards (each gets its own code)
          return Array.from({ length: qty }, () => ({ ...card }));
        }),
        buyer_name: buyer.buyer_name.trim(),
        buyer_email: buyer.buyer_email.trim(),
        buyer_phone: buyer.buyer_phone.trim(),
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null,
      };
      const res = await axios.post(`${API}/gift-cards/cart-checkout`, payload);
      // Clear the cart locally before redirecting
      clearCart();
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  const finalTotal = appliedPromo ? appliedPromo.final_amount : totalAmount;

  return (
    <div className="min-h-screen pb-24 pt-8 bg-gradient-to-b from-cream to-white">
      <Helmet>
        <title>Mon Panier — Cartes Cadeaux | Hilanta</title>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/carte-cadeau" className="inline-flex items-center gap-2 text-warm-brown-light hover:text-terracotta mb-4 text-sm">
          <ArrowLeft size={16} />
          Continuer à ajouter des cartes
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl text-warm-brown mb-2 flex items-center gap-3">
          <ShoppingBag size={32} className="text-terracotta" />
          Mon Panier
        </h1>
        <p className="font-body text-warm-brown-light mb-8">
          {items.length === 0
            ? 'Aucune carte cadeau pour l\'instant.'
            : `${itemCount} carte${itemCount > 1 ? 's' : ''} cadeau en attente de paiement.`}
        </p>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow p-12 text-center">
            <Gift size={48} className="mx-auto mb-4 opacity-30 text-warm-brown" />
            <p className="text-warm-brown-light mb-6">Votre panier est vide.</p>
            <Link to="/carte-cadeau">
              <Button className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-6">
                Choisir une carte cadeau
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it, idx) => {
              const isDuoCount = Object.values(it.duoServices || {}).filter(Boolean).length;
              const unit = Number(it.unit_total || 0);
              const qty = it.quantity || 1;
              const lineTotal = unit * qty;
              return (
                <div key={it.id} className="bg-white rounded-2xl shadow p-5 sm:p-6 border border-warm-brown/10" data-testid={`cart-item-${idx}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-heading text-lg text-warm-brown">
                        Pour <span className="text-terracotta font-bold">{it.recipient_name || '—'}</span>
                      </p>
                      {isDuoCount > 0 && (
                        <span className="text-xs bg-terracotta/10 text-terracotta px-2 py-0.5 rounded-full font-medium">
                          DUO ×{isDuoCount}
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-2xl text-terracotta font-bold" data-testid={`cart-line-total-${idx}`}>
                        {lineTotal.toFixed(2)} €
                      </p>
                      {qty > 1 && (
                        <p className="text-xs text-warm-brown-light">
                          {qty} × {unit.toFixed(2)} €
                        </p>
                      )}
                      <button
                        onClick={() => removeItem(it.id)}
                        className="text-red-500 hover:text-red-700 text-xs mt-1 inline-flex items-center gap-1"
                        data-testid={`cart-remove-${idx}`}
                      >
                        <Trash2 size={12} /> Retirer
                      </button>
                    </div>
                  </div>
                  <ul className="text-sm text-warm-brown-light space-y-0.5">
                    {it.services.map((s) => (
                      <li key={s.id} className="flex items-center gap-1">
                        <span className="text-terracotta">•</span>
                        {s.name}
                        {it.duoServices?.[s.id] && <span className="ml-1 text-xs text-terracotta">(DUO)</span>}
                        <span className="ml-2 text-xs">— {s.duration} min</span>
                      </li>
                    ))}
                  </ul>
                  {it.personal_message && (
                    <p className="mt-3 text-sm italic text-warm-brown-light bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                      « {it.personal_message} »
                    </p>
                  )}
                  {/* Quantity controls */}
                  <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-warm-brown-light">Quantité</span>
                      <div className="inline-flex items-center rounded-full border border-warm-brown/20 bg-cream/40 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => decrementItem(it.id)}
                          disabled={qty <= 1}
                          className="px-3 py-1.5 hover:bg-terracotta/10 text-warm-brown disabled:opacity-40 disabled:cursor-not-allowed transition"
                          aria-label="Diminuer la quantité"
                          data-testid={`cart-qty-decrement-${idx}`}
                        >
                          <Minus size={16} />
                        </button>
                        <span
                          className="px-4 py-1.5 font-semibold text-warm-brown min-w-[2.5rem] text-center"
                          data-testid={`cart-qty-value-${idx}`}
                        >
                          {qty}
                        </span>
                        <button
                          type="button"
                          onClick={() => incrementItem(it.id)}
                          disabled={qty >= 20}
                          className="px-3 py-1.5 hover:bg-terracotta/10 text-warm-brown disabled:opacity-40 disabled:cursor-not-allowed transition"
                          aria-label="Augmenter la quantité"
                          data-testid={`cart-qty-increment-${idx}`}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                    {qty > 1 && (
                      <p className="text-xs text-warm-brown-light italic">
                        {qty} cartes seront générées avec des codes uniques
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Buyer info + checkout */}
            <form onSubmit={handleCheckout} className="bg-white rounded-2xl shadow p-6 mt-6 space-y-5">
              <h2 className="font-heading text-xl text-warm-brown">Vos coordonnées</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Votre nom <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-brown-light" />
                    <Input value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="pl-10" required data-testid="cart-buyer-name" />
                  </div>
                </div>
                <div>
                  <Label>Votre email <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-brown-light" />
                    <Input type="email" value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="pl-10" required data-testid="cart-buyer-email" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>Votre téléphone <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-brown-light" />
                    <Input type="tel" value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} className="pl-10" required data-testid="cart-buyer-phone" />
                  </div>
                </div>
              </div>

              {/* Promo code */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Tag size={16} className="text-rose-600" />
                  Code promo (optionnel)
                </Label>
                {!appliedPromo ? (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                      placeholder="EX : NOEL2026"
                      className="rounded-xl font-mono uppercase"
                      data-testid="cart-promo-input"
                      maxLength={32}
                    />
                    <Button type="button" onClick={handleApplyPromo} disabled={validatingPromo} variant="outline" className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50 px-6" data-testid="cart-promo-apply">
                      {validatingPromo ? <Loader2 className="animate-spin" size={18} /> : 'Appliquer'}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between" data-testid="cart-promo-applied">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                      <Check size={16} /> {appliedPromo.code} : -{appliedPromo.amount_off.toFixed(2)} €
                    </div>
                    <button type="button" onClick={() => { setAppliedPromo(null); setPromoInput(''); }} className="text-emerald-700 hover:bg-emerald-100 p-1 rounded-full">
                      <X size={16} />
                    </button>
                  </div>
                )}
                {promoError && <p className="text-sm text-red-600 mt-1">{promoError}</p>}
              </div>

              {/* Total */}
              <div className="bg-terracotta/5 border border-terracotta/30 rounded-xl p-4 space-y-1">
                <div className="flex justify-between text-sm text-warm-brown-light">
                  <span>Sous-total ({itemCount} carte{itemCount > 1 ? 's' : ''})</span>
                  <span data-testid="cart-subtotal">{totalAmount.toFixed(2)} €</span>
                </div>
                {appliedPromo && (
                  <div className="flex justify-between text-sm text-emerald-700 font-medium">
                    <span>Code {appliedPromo.code}</span>
                    <span>-{appliedPromo.amount_off.toFixed(2)} €</span>
                  </div>
                )}
                <div className="border-t border-terracotta/20 my-1" />
                <div className="flex justify-between items-baseline">
                  <span className="font-heading text-warm-brown">Total</span>
                  <span className="font-heading text-2xl text-terracotta font-bold">{finalTotal.toFixed(2)} €</span>
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-full py-6 text-lg" data-testid="cart-checkout-btn">
                {submitting ? <><Loader2 className="animate-spin mr-2" size={20} /> Traitement…</> : `Payer ${finalTotal.toFixed(2)} €`}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
